import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, ArrowRight, Star, Sparkles, ShoppingBag, Eye, 
  TrendingUp, Crown, Heart, Zap, Award, Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { isRTL, useLanguageStore } from "@/stores/languageStore";
import { fetchProducts, ShopifyProduct } from "@/lib/shopify";
import { getProductTitle, getProductDescriptionPreview } from "@/lib/productTranslations";

// Car cover images
import carCover1 from "@/assets/car-cover-1.jpg";

// Dash cam images
import dashCam1 from "@/assets/dash-cam-1.jpg";

// Book reading light video
import bookReadingLightVideo from "@/assets/book-reading-light-video-1.mp4";

const featuredProductHandles = [
  "כיסוי-רכב-אוטומטי-חשמלי",
  "קופסת-תצוגה-מוארת-לנעליים-fd-102",
  "מנורת-קריאה-לספר-תאורה-ישירה-על-הדף",
  "מצלמת-דרך-חכמה-לרכב-3-עדשות",
  "שידת-לילה-חכמה-יוקרתית-עם-טעינה-אלחוטית-ותאורת-לד",
  "בקבוק-מים-ואוכל-משולב-לכלבים",
  "מצלמת-אקסטרים-ספורט-1080p",
  "סט-כלים-מקצועי"
];

// Handles that should NOT show stars (scanners)
const noStarsHandles = [
  "thinkscan-plus-s4-סורק-אבחון-לרכב",
  "thinkscan-plus-s7-סורק-אבחון-מקצועי-לרכב"
];

// Handles that should show video instead of image
const videoProductHandles: Record<string, string> = {
  "מנורת-קריאה-לספר-תאורה-ישירה-על-הדף": bookReadingLightVideo,
};

const fallbackImages: Record<string, string> = {
  "כיסוי-רכב-אוטומטי-חשמלי": carCover1,
  "מצלמת-דרך-חכמה-לרכב-3-עדשות": dashCam1,
};

// Original prices for featured products (for strikethrough pricing)
const ORIGINAL_PRICES: Record<string, number> = {
  "שידת-לילה-חכמה-יוקרתית-עם-טעינה-אלחוטית-ותאורת-לד": 850,
  "קופסת-תצוגה-מוארת-לנעליים-fd-102": 420,
};

// Floating particles component
const FloatingParticle = ({ delay, size, left }: { delay: number; size: number; left: string }) => (
  <motion.div
    className="absolute rounded-full bg-primary/20"
    style={{ width: size, height: size, left, top: '20%' }}
    initial={{ opacity: 0, y: 0 }}
    animate={{
      opacity: [0, 0.6, 0.6, 0],
      y: [0, -150, -300],
      x: [0, Math.random() * 30 - 15],
    }}
    transition={{
      duration: 8,
      delay,
      repeat: Infinity,
      ease: "easeOut",
    }}
  />
);

export const FeaturedProductsSection = () => {
  const { t, language } = useLanguageStore();
  const rtl = isRTL(language);
  const ArrowIcon = rtl ? ArrowLeft : ArrowRight;
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeProduct, setActiveProduct] = useState(0);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const results = await fetchProducts(100);
        const filteredProducts = results.products.filter(p => 
          featuredProductHandles.includes(p.node.handle)
        );
        filteredProducts.sort((a, b) => 
          featuredProductHandles.indexOf(a.node.handle) - featuredProductHandles.indexOf(b.node.handle)
        );
        setProducts(filteredProducts);
      } catch (error) {
        console.error("Error loading featured products:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Auto-rotate active product for hero spotlight
  useEffect(() => {
    if (products.length === 0) return;
    const interval = setInterval(() => {
      setActiveProduct((prev) => (prev + 1) % Math.min(3, products.length));
    }, 5000);
    return () => clearInterval(interval);
  }, [products.length]);

  if (isLoading) {
    return (
      <section className="py-24 bg-gradient-to-b from-background via-muted/30 to-background">
        <div className="container">
          <div className="text-center mb-16">
            <div className="h-12 w-80 bg-muted animate-pulse rounded-xl mx-auto mb-6" />
            <div className="h-6 w-[500px] max-w-full bg-muted animate-pulse rounded mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-[3/4] bg-muted animate-pulse rounded-3xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  const heroProduct = products[activeProduct];
  const heroImageUrl = heroProduct?.node.images?.edges?.[0]?.node?.url || fallbackImages[heroProduct?.node.handle];
  const heroPrice = parseFloat(heroProduct?.node.priceRange.minVariantPrice.amount || "0");

  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-b from-background via-muted/20 to-background">
      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <FloatingParticle key={i} delay={i * 1.5} size={4 + Math.random() * 6} left={`${10 + i * 12}%`} />
        ))}
      </div>

      {/* Background Glow Effects */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-1/4 left-1/6 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/6 w-[500px] h-[500px] bg-accent/10 rounded-full blur-[100px]" />
      </div>

      <div className="container relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          {/* Premium Badge */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-3 mb-8"
          >
            <Badge className="px-5 py-2.5 text-sm bg-gradient-to-r from-primary/20 to-accent/20 text-primary border-primary/30 font-semibold shadow-lg backdrop-blur-sm">
              <Crown className="w-4 h-4 ml-2 text-primary" />
              {t('home.featuredBadge')}
            </Badge>
          </motion.div>
          
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              {t('home.featuredTitle')}
            </span>
          </h2>
          
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            {t('home.featuredDescription')}
          </p>

          {/* Trust Indicators */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap justify-center gap-6 mt-10"
          >
            {[
              { icon: TrendingUp, text: t('featured.trending') || 'מוצרים פופולריים' },
              { icon: Award, text: t('featured.quality') || 'איכות מעולה' },
              { icon: Zap, text: t('featured.fastDelivery') || 'משלוח לכל הארץ' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-muted-foreground">
                <item.icon className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Hero Product Spotlight */}
        {products.length >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-20"
          >
            <div className="relative bg-gradient-to-br from-card via-card to-muted/50 rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Image Side */}
                <div className="relative aspect-square lg:aspect-auto overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeProduct}
                      src={heroImageUrl}
                      alt={getProductTitle(heroProduct?.node, language)}
                      className="w-full h-full object-cover"
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.6 }}
                    />
                  </AnimatePresence>
                  
                  {/* Overlay Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-background/20" />
                  
                  {/* Floating Badge */}
                  <motion.div 
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="absolute top-6 left-6"
                  >
                    <Badge className="bg-primary text-primary-foreground font-bold px-4 py-2 shadow-xl">
                      <Sparkles className="w-4 h-4 ml-1.5" />
                      {t('featured.spotlight') || 'מוצר מומלץ'}
                    </Badge>
                  </motion.div>
                </div>

                {/* Content Side */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeProduct}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                        {getProductTitle(heroProduct?.node, language)}
                      </h3>
                      
                      <p className="text-muted-foreground text-base lg:text-lg mb-6 leading-relaxed line-clamp-3">
                        {getProductDescriptionPreview(heroProduct?.node, language)}
                      </p>

                      {/* Price */}
                      <div className="flex items-center gap-4 mb-8">
                        <span className="text-4xl lg:text-5xl font-bold text-primary">
                          ₪{heroPrice.toFixed(0)}
                        </span>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} className="w-5 h-5 fill-primary text-primary" />
                          ))}
                        </div>
                      </div>

                      {/* CTA Buttons */}
                      <div className="flex flex-wrap gap-4">
                        <Link to={`/product/${heroProduct?.node.handle}`}>
                          <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl hover:shadow-2xl transition-all px-8 py-6 text-lg">
                            <Eye className="w-5 h-5 ml-2" />
                            {t('products.viewDetails') || 'צפה במוצר'}
                          </Button>
                        </Link>
                        <Link to="/products">
                          <Button size="lg" variant="outline" className="border-primary/30 hover:bg-primary/5 px-6 py-6 text-lg">
                            {t('home.viewAll') || 'כל המוצרים'}
                          </Button>
                        </Link>
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Spotlight Navigation Dots */}
                  <div className="flex gap-3 mt-10">
                    {products.slice(0, 3).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveProduct(idx)}
                        className={`transition-all duration-300 rounded-full ${
                          activeProduct === idx
                            ? "w-10 h-3 bg-primary"
                            : "w-3 h-3 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 lg:gap-8">
        {products.map((product, idx) => {
            const handle = product.node.handle;
            const imageUrl = product.node.images?.edges?.[0]?.node?.url || fallbackImages[handle];
            const videoUrl = videoProductHandles[handle];
            const price = parseFloat(product.node.priceRange.minVariantPrice.amount);
            // Use hardcoded original prices or Shopify compareAtPrice
            const shopifyCompareAt = product.node.compareAtPriceRange?.minVariantPrice?.amount 
              ? parseFloat(product.node.compareAtPriceRange.minVariantPrice.amount)
              : null;
            const originalPrice = ORIGINAL_PRICES[handle] || shopifyCompareAt;
            const compareAtPrice = originalPrice && originalPrice > price ? originalPrice : null;
            const discount = compareAtPrice 
              ? Math.round((1 - price / compareAtPrice) * 100)
              : 0;
            const isHovered = hoveredIndex === idx;

            return (
              <motion.div
                key={product.node.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6, ease: "easeOut" }}
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="group"
              >
                <Link to={`/product/${handle}`} className="block">
                  <motion.div 
                    className="relative bg-card rounded-2xl overflow-hidden border border-border/50 shadow-lg transition-all duration-500 group-hover:shadow-2xl group-hover:border-primary/30"
                    whileHover={{ y: -8 }}
                  >
                    {/* Image/Video Container */}
                    <div className="relative aspect-square overflow-hidden">
                      {videoUrl ? (
                        <>
                          <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          >
                            <source src={videoUrl} type="video/mp4" />
                          </video>
                          {/* Video Play Badge */}
                          <div className="absolute top-4 left-4 p-2 rounded-full bg-primary/90 shadow-lg">
                            <Play className="w-3 h-3 text-primary-foreground fill-current" />
                          </div>
                        </>
                      ) : (
                        <img
                          src={imageUrl}
                          alt={getProductTitle(product.node, language)}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      )}
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                      
                      {/* Discount Badge */}
                      {discount > 0 && (
                        <motion.div 
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="absolute top-4 right-4"
                        >
                          <Badge className="bg-destructive text-destructive-foreground font-bold px-3 py-1.5 text-sm shadow-xl">
                            -{discount}%
                          </Badge>
                        </motion.div>
                      )}
                      
                      {/* Quick View Overlay */}
                      <motion.div 
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: isHovered ? 0 : 30, opacity: isHovered ? 1 : 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="absolute bottom-0 left-0 right-0 p-4"
                      >
                        <div className="flex items-center justify-center gap-2 bg-background/95 backdrop-blur-md py-3.5 rounded-xl text-sm font-semibold text-foreground shadow-2xl border border-border/50">
                          <Eye className="w-4 h-4 text-primary" />
                          {t('products.viewProduct') || 'צפה במוצר'}
                        </div>
                      </motion.div>

                      {/* Love button */}
                      <motion.button
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: isHovered ? 1 : 0, opacity: isHovered ? 1 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-4 left-4 p-2.5 rounded-full bg-background/90 backdrop-blur-sm shadow-lg border border-border/50"
                        onClick={(e) => e.preventDefault()}
                      >
                        <Heart className="w-4 h-4 text-muted-foreground hover:text-destructive transition-colors" />
                      </motion.button>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      {/* Title */}
                      <h3 className="font-semibold text-foreground text-base line-clamp-2 mb-3 group-hover:text-primary transition-colors duration-300 min-h-[3rem]">
                        {getProductTitle(product.node, language)}
                      </h3>

                      {/* Price & Rating Row */}
                      <div className="flex items-center justify-between">
                        {/* Price */}
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-bold text-primary">
                            ₪{price.toFixed(0)}
                          </span>
                          {compareAtPrice !== null && compareAtPrice > price && (
                            <span className="text-sm text-muted-foreground line-through">
                              ₪{compareAtPrice.toFixed(0)}
                            </span>
                          )}
                        </div>

                        {/* Rating Stars */}
                        {!noStarsHandles.includes(handle) && (
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className="w-3.5 h-3.5 fill-primary text-primary" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16"
        >
          <Link to="/products">
            <Button 
              size="lg" 
              className="group bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground px-10 py-7 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 rounded-xl"
            >
              <ShoppingBag className={`w-5 h-5 ${rtl ? 'ml-3' : 'mr-3'}`} />
              {t('home.viewAllProducts') || 'לכל המוצרים'}
              <ArrowIcon className={`w-5 h-5 ${rtl ? 'mr-3' : 'ml-3'} transition-transform group-hover:${rtl ? '-translate-x-2' : 'translate-x-2'}`} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
